import React from 'react';

import { Box, ScrollView } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { useAppTakeoverPdp } from 'features/app-takeover/hooks/use-app-takeover';

import { FullWidthAutoHeightImage } from 'components/picture';
import { NutritionInformation } from 'components/product-detail-v2/nutrition-information';
import { ProductCta } from 'components/product-detail-v2/product-cta';
import { ProductUpsell } from 'components/product-detail-v2/product-upsell';
import { transformProductWizardStepToEventAttr } from 'components/product-detail-v2/utils';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { useProductWizardContext } from 'state/product-wizard-v2';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { ProductWizardStep } from 'state/product-wizard-v2/types';
import { primitive } from 'styles/constants';
import { isCombo } from 'utils/menu';

import { ProductDetailHeader } from '../product-detail-header';

import { OrderSummaryPanel } from './order-summary-panel';
import {
  ProductOrderReviewContainer,
  ProductOrderReviewInnerLayout,
  ProductOrderReviewWrapper,
  ProductReviewLayoutContainer,
} from './styled';
import { IProductOrderReview } from './types';

export const ProductOrderReview: React.FC<IProductOrderReview> = ({ modifierSelections }) => {
  const { back } = useRouter();
  const {
    currentStep,
    goToHome,
    goToCustomizeProduct,
    isProductReviewFromCart,
    id,
  } = useProductWizardFlow();

  const {
    backgroundColor: takeoverBg,
    backgroundImage,
    summaryPageBackgroundImage,
  } = useAppTakeoverPdp(id);
  const {
    menuObject,
    productWizardFlowSteps,
    resetProductWizard,
    setIsBackNavigation,
    selectedProduct,
    userSelections,
  } = useProductWizardContext();

  const isMobile = useIsMobileBreakpoint();

  const foundFirstStep = productWizardFlowSteps.find(step => !!step.isFirstStep);
  const shouldRedirectToHome = !foundFirstStep && !isProductReviewFromCart;

  React.useEffect(() => {
    if (shouldRedirectToHome) {
      resetProductWizard();
      goToHome();
    }
  }, [goToHome, resetProductWizard, shouldRedirectToHome]);

  const lastScreen = productWizardFlowSteps.at(-1);

  const handleBackNavigation = React.useCallback(() => {
    setIsBackNavigation(true);

    if (isProductReviewFromCart) {
      return back();
    }

    return goToCustomizeProduct({
      slotKey: lastScreen?.id ?? '',
      step: ProductWizardStep.ProductCustomization,
    });
  }, [back, goToCustomizeProduct, isProductReviewFromCart, lastScreen?.id, setIsBackNavigation]);

  const productDetailHeaderTitle = isCombo(selectedProduct)
    ? t('reviewYourMeal')
    : t('reviewYourSelection');

  const backNavigationLabel =
    (lastScreen && userSelections.comboSlot?.get(lastScreen.id)?.selectedItem?.name.locale) ?? '';

  if (shouldRedirectToHome) {
    // redirecting don't display janky UI
    return null;
  }

  return (
    <ProductOrderReviewWrapper>
      <ProductDetailHeader
        title={productDetailHeaderTitle}
        currentStep={currentStep}
        onPress={handleBackNavigation}
        backgroundColor={primitive.$white}
        backNavigationLabel={t('productDetailPickerCustomizationGoBack', {
          screen: backNavigationLabel,
        })}
        eventAttributes={{ name: transformProductWizardStepToEventAttr(currentStep) }}
      />

      <ScrollView showsVerticalScrollIndicator backgroundColor={takeoverBg || primitive.$white}>
        {(summaryPageBackgroundImage || backgroundImage) && (
          <FullWidthAutoHeightImage
            source={summaryPageBackgroundImage || backgroundImage}
            style={{ position: 'absolute' }}
          />
        )}
        <Box width={'full'}>
          <ProductReviewLayoutContainer isFullContainer={isMobile}>
            <ProductOrderReviewInnerLayout>
              <OrderSummaryPanel />
            </ProductOrderReviewInnerLayout>
          </ProductReviewLayoutContainer>
        </Box>

        <ProductReviewLayoutContainer
          isFullContainer={isMobile}
          backgroundColor={takeoverBg || primitive.$mayoEggWhite}
        >
          <ProductOrderReviewInnerLayout>
            <ProductOrderReviewContainer>
              <ProductUpsell />
              <NutritionInformation />
            </ProductOrderReviewContainer>
          </ProductOrderReviewInnerLayout>
        </ProductReviewLayoutContainer>
      </ScrollView>

      <ProductCta
        currentStep={currentStep}
        menuObject={menuObject}
        modifierSelections={modifierSelections}
      />
    </ProductOrderReviewWrapper>
  );
};
